import { useTranslation } from "react-i18next";
import { RotateCcw } from "lucide-react";
import { useLanguageStore } from "../../../core/store/languageStore";
import { formatHistoryDate } from "../../../utils/dateUtils";
import { badgeClasses } from "../../constants/badgeClasses";
import {
  authorLabel,
  type DeletedRecord,
} from "../../../core/history/entities/HistoryVersion";

interface DeletedRecordListProps {
  records: DeletedRecord[];
  canRestore: boolean;
  onRestore: (record: DeletedRecord) => void;
  /// Profondeur d'imbrication, pour l'indentation des descendants.
  depth?: number;
}

/// Liste des éléments liés supprimés, avec leur descendance imbriquée.
///
/// Une partie effacée en cascade emporte ses questions : celles-ci n'ont plus de page parente,
/// elles sont donc affichées sous la partie supprimée. Elles n'y sont pas restaurables séparément
/// ([DeletedRecord.restorable] est décidé par le serveur) : restaurer la partie les ramène avec
/// elle, alors qu'une restauration isolée échouerait faute de parent existant.
///
/// Un élément déjà restauré reste listé, marqué comme tel : la trace de sa suppression demeure dans
/// l'historique, et le signaler évite d'en créer un doublon.
export function DeletedRecordList({
  records,
  canRestore,
  onRestore,
  depth = 0,
}: DeletedRecordListProps) {
  const { t } = useTranslation();
  const { language } = useLanguageStore();

  return (
    <ul
      className={
        depth > 0
          ? "mt-1.5 space-y-1.5 border-s border-gray-200 ps-3 dark:border-gray-700"
          : "space-y-1.5"
      }
    >
      {records.map((record) => {
        const isRestored = record.status === "restored";

        return (
          <li key={record.versionId}>
            <div
              className={`flex flex-wrap items-center justify-between gap-x-3 gap-y-2 rounded-lg border px-3 py-2.5 ${
                isRestored
                  ? "border-emerald-200 bg-emerald-50/40 dark:border-emerald-900/50 dark:bg-emerald-950/20"
                  : "border-gray-200 dark:border-gray-700"
              }`}
            >
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-2 text-sm font-medium text-gray-800 dark:text-gray-200">
                  <span className="truncate">{record.label}</span>
                  {isRestored && (
                    <span className={`shrink-0 ${badgeClasses.success}`}>
                      {t("history.restored")}
                    </span>
                  )}
                </p>
                <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                  {t(`history.collection.${record.collection}`)}
                  {" · "}
                  {/* Un orphelin déduit d'une ligne `update` n'a pas de date de suppression fiable :
                      seule la dernière modification connue est datée. */}
                  {record.action === "delete"
                    ? t("history.deletedOn", {
                        date: formatHistoryDate(record.deletedAt, language),
                      })
                    : t("history.lastSeenOn", {
                        date: formatHistoryDate(record.deletedAt, language),
                      })}
                  {" · "}
                  {authorLabel(record.author, t("history.unknownAuthor"))}
                </p>
                {isRestored && (
                  <p className="mt-0.5 text-xs text-emerald-700 dark:text-emerald-400">
                    {t("history.restoredOn", {
                      date: formatHistoryDate(record.restoredAt, language),
                      author: record.restoredBy
                        ? authorLabel(
                            record.restoredBy,
                            t("history.unknownAuthor"),
                          )
                        : t("history.unknownAuthor"),
                    })}
                  </p>
                )}
              </div>

              {canRestore && record.restorable && (
                <button
                  type="button"
                  onClick={() => onRestore(record)}
                  className="flex shrink-0 items-center gap-1.5 rounded-3xl border border-gray-300 px-2.5 py-1 text-xs text-gray-900 transition-colors hover:bg-blue-600 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:border-gray-600 dark:text-gray-100 dark:hover:bg-blue-600"
                >
                  <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
                  {t("history.restore")}
                </button>
              )}

              {/* Sans explication, l'absence de bouton se lit comme un droit manquant. */}
              {canRestore && !record.restorable && !isRestored && (
                <span className="shrink-0 text-xs text-gray-400 dark:text-gray-500">
                  {t("history.restoredWithParent")}
                </span>
              )}
            </div>

            {record.children.length > 0 && (
              <DeletedRecordList
                records={record.children}
                canRestore={canRestore}
                onRestore={onRestore}
                depth={depth + 1}
              />
            )}
          </li>
        );
      })}
    </ul>
  );
}
